import { ChevronDown, ListFilter } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ChangeTypeCount } from "../api/types";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";

const CHANGE_TYPE_ORDER = [
  "SegmentCreated",
  "RulesChanged",
  "TypeChanged",
  "InversionChanged",
  "VacanciesAdded",
  "VacanciesRemoved",
  "OverridesChanged",
  "SegmentArchived",
] as const;

function sortChangeTypeCounts(
  changeTypeCounts: ChangeTypeCount[],
): ChangeTypeCount[] {
  const known = CHANGE_TYPE_ORDER.map((type) =>
    changeTypeCounts.find((c) => c.changeType === type),
  ).filter((c): c is ChangeTypeCount => c !== undefined);
  const unknown = changeTypeCounts
    .filter(
      (c) =>
        !CHANGE_TYPE_ORDER.includes(
          c.changeType as (typeof CHANGE_TYPE_ORDER)[number],
        ),
    )
    .sort((a, b) => a.changeType.localeCompare(b.changeType));
  return [...known, ...unknown];
}

type SegmentHistoryFiltersProps = {
  selected: string[];
  onChange: (changeTypes: string[]) => void;
  changeTypeCounts: ChangeTypeCount[];
};

export function SegmentHistoryFilters({
  selected,
  onChange,
  changeTypeCounts,
}: SegmentHistoryFiltersProps) {
  const { t } = useTranslation();

  const sortedCounts = useMemo(
    () => sortChangeTypeCounts(changeTypeCounts),
    [changeTypeCounts],
  );

  const handleToggle = (type: string) => {
    if (selected.includes(type))
      return onChange(selected.filter((s) => s !== type));
    onChange([...selected, type]);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-1.5">
          <ListFilter className="h-4 w-4" />
          {t("admin.segments.history.quickFilters")}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72" align="start">
        {sortedCounts.map((entry) => (
          <DropdownMenuCheckboxItem
            key={entry.changeType}
            checked={selected.includes(entry.changeType)}
            onCheckedChange={() => handleToggle(entry.changeType)}
            onSelect={(e) => e.preventDefault()}
            count={entry.count}
          >
            {t(`admin.segments.history.changeTypes.${entry.changeType}`, {
              defaultValue: entry.changeType,
            })}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
